import { useState } from 'react';

// @ts-ignore - JSX component
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';

interface PhoneNotesDialogProps {
    isOpen: boolean;
    onClose: () => void;
    initialNote?: string | null;
    professorName?: string | null;
    onSave: (note: string) => void;
    loading: boolean;
}

export function PhoneNotesDialog({
    isOpen,
    onClose,
    initialNote,
    professorName,
    onSave,
    loading
}: PhoneNotesDialogProps) {
    /* Semintarea trebuie sa acopere DOUA cai, si prima m-a pacalit deja o data:
       componenta e randata de parinte doar cand exista o cerere selectata, deci la
       prima deschidere se MONTEAZA cu `isOpen` deja `true`. Intr-un asemenea mont,
       blocul de tranzitie de mai jos nu ruleaza (nu exista tranzitie), deci daca
       `note` porneste de la `''` ramane gol, iar `notaInitiala` are notita de pe
       server — si dialogul se deschide gol pe randul care ARE notita, cu garda
       pornita din prima secunda. Masurat, nu dedus. De aceea valoarea de start se
       ia din `isOpen`, nu se presupune inchis. */
    const [note, setNote] = useState(isOpen ? (initialNote || '') : '');

    /* Semintarea casutei si valoarea de referinta se fac la TRANZITIA lui `isOpen`,
       in randare, nu dintr-un `useEffect`.

       De ce nu efect: fratele acestui dialog, `users/components/NotesDialog.tsx`,
       a avut exact garda de mai jos sprijinita pe un `useRef` fixat din efect, si
       pica taman pe cazul obisnuit — un rand care ARE deja notita. Referinta se
       fixeaza dupa randare, iar schimbarea ei NU declanseaza o re-randare, deci
       valoarea calculata din ea ramane cea veche in inchiderea handlerului.
       Aici, ajustarea la schimbarea propului declanseaza re-randare, deci si
       `note`, si `notaInitiala` sunt corecte din chiar randarea deschiderii.

       Efectul de dinainte mai avea o problema: `[isOpen, initialNote]` re-semanta
       casuta ori de cate ori se schimba propul, inclusiv cat timp dialogul era
       deschis — adica peste ce tocmai scria omul. */
    const [eraDeschis, setEraDeschis] = useState(isOpen);
    const [notaInitiala, setNotaInitiala] = useState(isOpen ? (initialNote || '') : '');
    if (isOpen !== eraDeschis) {
        setEraDeschis(isOpen);
        setNotaInitiala(isOpen ? (initialNote || '') : '');
        setNote(isOpen ? (initialNote || '') : '');
    }
    const modificat = note !== notaInitiala;

    const handleSave = () => {
        onSave(note);
    };

    const handleClose = () => {
        setNote('');
        onClose();
    };

    return (
        /* `onOpenChange` primeste un BOOLEAN. Varianta de dinainte ii dadea direct
           `handleClose`, deci inchidea si la deschidere — mergea doar pentru ca
           dialogul n-are `DialogTrigger`, deci evenimentul nu se produce niciodata
           cu `true`. Adaugarea unui trigger l-ar fi facut sa nu se mai deschida.

           `onInteractOutside`: clicul in afara panoului inchidea dialogul si
           pierdea textul tastat, fara avertisment. Se opreste cat timp casuta
           difera de valoarea cu care s-a deschis. `Escape` ramane neconditionat —
           e gestul deliberat de abandon. Aceeasi decizie ca in `NotesDialog`. */
        <Dialog open={isOpen} onOpenChange={(deschis) => { if (!deschis) handleClose(); }}>
            <DialogContent
                className="sm:max-w-[500px]"
                onInteractOutside={(e) => { if (modificat) e.preventDefault(); }}
            >
                <DialogHeader>
                    <DialogTitle>Notițe Telefonice</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {/* User Info */}
                    <div>
                        <label className="text-sm font-medium text-gray-700">Profesor:</label>
                        <p className="text-sm text-gray-900">{professorName || '-'}</p>
                    </div>

                    {/* Notes Textarea */}
                    <div>
                        <label className="text-sm font-medium text-gray-700">
                            Notițe despre conversații telefonice:
                        </label>
                        {/* `text-base md:text-sm`, ca in `Input`-ul shadcn: sub 16px, iOS
                            mareste pagina la focus si nu o mai micsoreaza. Acelasi camp
                            era reparat in celelalte dialoguri si scapase aici. */}
                        <textarea
                            className="mt-1 w-full rounded-md border border-input px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-magenta md:text-sm"
                            rows={6}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Introduceți notițe despre conversațiile telefonice cu acest utilizator..."
                            disabled={loading}
                        />
                        <p className="mt-1 text-small text-gray-700">
                            Aceste notițe vor fi vizibile doar pentru administratori
                        </p>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 justify-end">
                        <button
                            onClick={handleClose}
                            disabled={loading}
                            className="flex h-11 items-center rounded-md border border-input bg-white px-4 text-small font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 md:h-9"
                        >
                            Anulare
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={loading}
                            className="flex h-11 items-center rounded-md bg-magenta px-4 text-small font-medium text-white hover:bg-magenta/90 disabled:cursor-not-allowed disabled:opacity-50 md:h-9"
                        >
                            Salvează
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
